import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import EmptyState from '@/components/common/EmptyState'
import { SEVERIDAD } from './estados'

const dinero = (n) =>
  `$${Number(n || 0).toLocaleString('es-SV', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/**
 * EL CUADRE — que las hojas encajen entre sí, antes de tocar la empresa del cliente.
 *
 * Es lo único que no se puede ver mirando una hoja sola, y por eso es la pantalla que
 * justifica el área de preparación entera: "340 facturas apuntan a 12 clientes que no están
 * en tu hoja de Clientes", "la cartera suma $84.320,15 y tú dices $84.900".
 *
 * El informe llega AGRUPADO POR MOTIVO, no por fila. Con 20.000 filas y tres motivos,
 * listar filas es tirarle el problema al asesor: doce nombres son una lista que el cliente
 * puede completar; 340 líneas repitiendo lo mismo, no.
 */
export default function Cuadre({ cuadre, cargando, onCuadrar, onDescargarCorrecciones }) {
  const [declarado, setDeclarado] = useState({ cartera: '', deuda: '', inventario: '' })

  const campos = [
    { clave: 'cartera', etiqueta: 'Cartera (lo que le deben)', suma: cuadre?.sumas?.cartera },
    { clave: 'deuda', etiqueta: 'Deuda con proveedores', suma: cuadre?.sumas?.deuda },
    { clave: 'inventario', etiqueta: 'Inventario valorado', suma: cuadre?.sumas?.inventario },
  ]

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div>
            <p className="text-body font-medium text-neutral-900 dark:text-neutral-50">¿Cuánto dice el cliente que tiene?</p>
            <p className="text-caption text-neutral-500 dark:text-neutral-400">
              Opcional, pero es lo que evita la conversación incómoda de después. Si lo que suman sus
              archivos no coincide con lo que él espera, mejor saberlo ahora.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {campos.map((c) => (
              <div key={c.clave} className="space-y-1">
                <Label htmlFor={`decl-${c.clave}`}>{c.etiqueta}</Label>
                <Input
                  id={`decl-${c.clave}`}
                  inputMode="decimal"
                  placeholder="—"
                  value={declarado[c.clave]}
                  onChange={(e) => setDeclarado((d) => ({ ...d, [c.clave]: e.target.value }))}
                />
                {c.suma != null && (
                  <p className="text-caption tabular-nums text-neutral-500 dark:text-neutral-400">Los archivos suman {dinero(c.suma)}</p>
                )}
              </div>
            ))}
          </div>

          <Button
            onClick={() =>
              onCuadrar(
                Object.fromEntries(
                  Object.entries(declarado)
                    .filter(([, v]) => String(v).trim() !== '')
                    .map(([k, v]) => [k, Number(String(v).replace(/[^0-9.-]/g, ''))])
                )
              )
            }
            disabled={cargando}
          >
            {cargando ? 'Cuadrando…' : 'Cuadrar las hojas'}
          </Button>
        </CardContent>
      </Card>

      {cuadre && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={cuadre.bloqueantes > 0 ? 'danger' : 'success'}>
              {cuadre.bloqueantes} que impiden operar
            </Badge>
            <Badge variant="warning">{cuadre.avisos} para revisar</Badge>
            {onDescargarCorrecciones && (
              <Button variant="outline" size="sm" onClick={onDescargarCorrecciones}>
                Descargar el archivo marcado
              </Button>
            )}
          </div>

          {cuadre.hallazgos.length === 0 ? (
            <EmptyState
              icono={<CheckCircle2 size={28} />}
              titulo="Las hojas encajan"
              descripcion="Ninguna apunta a algo que falte, y las sumas cuadran. Se puede cargar."
            />
          ) : (
            <ul className="space-y-3">
              {cuadre.hallazgos.map((h) => {
                const sev = SEVERIDAD[h.severidad] || SEVERIDAD.aviso
                return (
                  <li key={h.clave}>
                    <Card>
                      <CardContent className="space-y-1.5 pt-5">
                        <div className="flex flex-wrap items-center gap-2">
                          {h.severidad === 'bloqueante' ? (
                            <AlertTriangle size={16} className="shrink-0 text-danger-700 dark:text-danger-300" />
                          ) : (
                            <Info size={16} className="shrink-0 text-warning-700 dark:text-warning-300" />
                          )}
                          <span className="font-medium text-neutral-900 dark:text-neutral-50">{h.titulo}</span>
                          <Badge variant={sev.variante}>{sev.etiqueta}</Badge>
                        </div>
                        {/* La CONSECUENCIA, no el conteo: "12 clientes ausentes" no mueve a
                            nadie; "340 facturas que se van a rechazar", sí. */}
                        <p className="text-body-sm text-neutral-600 dark:text-neutral-400">{h.consecuencia}</p>
                        {h.ejemplos?.length > 0 && (
                          <p className="text-caption text-neutral-500 dark:text-neutral-400">
                            {h.ejemplos.slice(0, 8).join(' · ')}
                            {h.cuantos > 8 && ` … y ${h.cuantos - 8} más`}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
